import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Button
} from '@material-ui/core';
import {
  Close,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  GetApp,
  PictureAsPdf
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import videoProgressService from '../../services/videoProgressService';
import './style.scss';

const VideoPlayerModal = ({ open, onClose, video, user, onProgressUpdate }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [error, setError] = useState(null);
  const [watchTime, setWatchTime] = useState(0);
  const [lastProgressSave, setLastProgressSave] = useState(0);
  
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const progressSaveIntervalRef = useRef(null);

  // Load existing video progress
  const loadVideoProgress = React.useCallback(async () => {
    try {
      const progressData = await videoProgressService.getVideoProgress(user.user.id, video.id);
      if (progressData.data && progressData.data.length > 0) {
        const progress = progressData.data[0];
        setCurrentTime(progress.currentTime || 0);
        setWatchTime(progress.timeWatched || 0);
        
        // Set video to saved position if available
        const videoElement = videoRef.current;
        if (videoElement && progress.currentTime > 0) {
          videoElement.currentTime = progress.currentTime;
        }
      }
    } catch (error) {
      console.error('Error loading video progress:', error);
    }
  }, [user?.user?.id, video?.id]);

  // Save progress every 10 seconds
  const saveProgress = React.useCallback(async (forceComplete = false) => {
    if (!user?.user?.id || !video?.id || !duration) return;
    
    try {
      const progressPercentage = forceComplete ? 100 : Math.round((currentTime / duration) * 100);
      const isCompleted = forceComplete || progressPercentage >= 95; // Consider 95%+ as completed
      
      const progressData = {
        userId: user.user.id,
        videoId: video.id,
        progress: progressPercentage,
        currentTime: currentTime,
        duration: duration,
        completed: isCompleted,
        category: video.category,
        title: video.title,
        timeWatched: watchTime + (currentTime - lastProgressSave)
      };
      
      await videoProgressService.updateVideoProgress(progressData);
      setLastProgressSave(currentTime);
      
      // Notify parent component of progress update
      if (onProgressUpdate) {
        onProgressUpdate(video.id, progressPercentage, isCompleted);
      }
      
      if (isCompleted && !forceComplete) {
        toast.success('🎉 Video completat cu succes!', {
          style: {
            background: 'linear-gradient(135deg, #904d71 0%, #6c3d51 100%)',
            color: 'white',
            fontWeight: '600'
          }
        });
      }
    } catch (error) {
      console.error('Error saving video progress:', error);
    }
  }, [user?.user?.id, video?.id, video?.category, video?.title, duration, currentTime, watchTime, lastProgressSave, onProgressUpdate]);

  useEffect(() => {
    if (open && video) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setError(null);
      setIsLoading(true);
      setWatchTime(0);
      setLastProgressSave(0);
      
      // Load existing progress for this video
      if (user?.user?.id && video.id) {
        loadVideoProgress();
      }
    }
  }, [open, video, user?.user?.id, loadVideoProgress]);

  useEffect(() => {
    if (open && video) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setError(null);
      setIsLoading(true);
      setWatchTime(0);
      setLastProgressSave(0);
      
      // Load existing progress for this video
      if (user?.user?.id && video.id) {
        loadVideoProgress();
      }
    }
  }, [open, video, user?.user?.id, loadVideoProgress]);

  // Progress saving interval
  useEffect(() => {
    if (isPlaying) {
      progressSaveIntervalRef.current = setInterval(() => {
        if (currentTime > 0 && duration > 0) {
          saveProgress();
        }
      }, 10000); // Save every 10 seconds
    } else if (progressSaveIntervalRef.current) {
      clearInterval(progressSaveIntervalRef.current);
    }

    return () => {
      if (progressSaveIntervalRef.current) {
        clearInterval(progressSaveIntervalRef.current);
      }
    };
  }, [isPlaying, currentTime, duration, saveProgress]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      const newTime = videoElement.currentTime;
      setCurrentTime(newTime);
      
      // Update watch time when playing
      if (isPlaying) {
        setWatchTime(prev => prev + (newTime - currentTime > 0 ? newTime - currentTime : 0));
      }
    };

    const handleDurationChange = () => {
      setDuration(videoElement.duration || 0);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleError = (e) => {
      console.error('Video error:', e);
      setError('Eroare la încărcarea videoclipului');
      setIsLoading(false);
      toast.error('Eroare la încărcarea videoclipului');
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Save progress as completed when video ends
      saveProgress(true);
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('durationchange', handleDurationChange);
    videoElement.addEventListener('canplay', handleCanPlay);
    videoElement.addEventListener('error', handleError);
    videoElement.addEventListener('ended', handleEnded);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('durationchange', handleDurationChange);
      videoElement.removeEventListener('canplay', handleCanPlay);
      videoElement.removeEventListener('error', handleError);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [video, currentTime, isPlaying, saveProgress]);

  const togglePlay = async () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    try {
      if (isPlaying) {
        await videoElement.pause();
        setIsPlaying(false);
      } else {
        // Ensure the video is loaded before trying to play
        if (videoElement.readyState >= 2) {
          await videoElement.play();
          setIsPlaying(true);
        } else {
          // Wait for the video to load enough data
          videoElement.addEventListener('canplay', async () => {
            try {
              await videoElement.play();
              setIsPlaying(true);
            } catch (playError) {
              console.error('Error playing video after canplay:', playError);
              toast.error('Eroare la redarea videoclipului');
            }
          }, { once: true });
        }
      }
    } catch (error) {
      console.error('Error toggling play:', error);
      toast.error('Eroare la controlarea videoclipului');
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const newMutedState = !isMuted;
    videoElement.muted = newMutedState;
    setIsMuted(newMutedState);
    
    // If unmuting, restore previous volume
    if (!newMutedState && volume === 0) {
      videoElement.volume = 0.5;
      setVolume(0.5);
    }
  };

  const handleVolumeChange = (event) => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const newVolume = parseFloat(event.target.value);
    videoElement.volume = newVolume;
    setVolume(newVolume);
    
    // Auto-unmute if volume is increased from 0
    if (newVolume > 0 && isMuted) {
      videoElement.muted = false;
      setIsMuted(false);
    }
  };

  const handleSeek = (event) => {
    const videoElement = videoRef.current;
    if (!videoElement || !duration) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const pos = (event.clientX - rect.left) / rect.width;
    const newTime = pos * duration;
    
    videoElement.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleFullscreen = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (videoElement.requestFullscreen) {
      videoElement.requestFullscreen();
    } else if (videoElement.webkitRequestFullscreen) {
      videoElement.webkitRequestFullscreen();
    } else if (videoElement.msRequestFullscreen) {
      videoElement.msRequestFullscreen();
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    
    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    // Set new timeout to hide controls
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const handleClose = () => {
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.pause();
    }
    setIsPlaying(false);
    
    // Save progress before closing
    if (currentTime > 0 && duration > 0) {
      saveProgress();
    }
    
    onClose();
  };

  const handleDownloadEbook = () => {
    if (video?.ebookUrl) {
      window.open(video.ebookUrl, '_blank');
      toast.success('Se deschide eBook-ul...');
    } else {
      toast.info('Nu există eBook disponibil pentru acest video');
    }
  };

  if (!video) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      className="video-player-modal"
    >
      <div className="video-modal-container">
        {/* Header */}
        <div className="video-modal-header">
          <div className="video-info">
            <Typography variant="h6" className="video-title">
              {video.title}
            </Typography>
            <div className="video-badges">
              <Chip
                size="small"
                label={video.category}
                className="category-chip"
              />
              <Chip
                size="small"
                label={video.requiredRank}
                className="rank-chip"
              />
            </div>
          </div>
          <IconButton
            className="close-button"
            onClick={handleClose}
          >
            <Close />
          </IconButton>
        </div>

        {/* Video Player */}
        <DialogContent className="video-content">
          <div
            className="video-player-container"
            onMouseMove={handleMouseMove}
          >
            {error ? (
              <div className="video-error">
                <Typography variant="h6" color="error">
                  {error}
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => window.location.reload()}
                  style={{ marginTop: '16px' }}
                >
                  Reîncarcă pagina
                </Button>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  className="video-player"
                  src={video.videoUrl}
                  poster={video.thumbnailUrl}
                  controls={false}
                  preload="metadata"
                  playsInline
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onLoadStart={() => setIsLoading(true)}
                  onLoadedData={() => setIsLoading(false)}
                  onCanPlay={() => setIsLoading(false)}
                  onClick={togglePlay}
                />

                {isLoading && (
                  <div className="video-loading">
                    <CircularProgress size={60} style={{ color: '#904d71' }} />
                    <Typography variant="body1" style={{ marginTop: '16px', color: '#904d71' }}>
                      Se încarcă videoclipul...
                    </Typography>
                  </div>
                )}

                {/* Custom Controls */}
                <div className={`video-controls ${showControls ? 'visible' : 'hidden'}`}>
                  {/* Progress Bar */}
                  <div className="progress-container">
                    <div 
                      className="progress-bar"
                      onClick={handleSeek}
                    >
                      <div 
                        className="progress-fill"
                        style={{ 
                          width: duration ? `${(currentTime / duration) * 100}%` : '0%' 
                        }}
                      />
                    </div>
                  </div>

                  {/* Control Buttons */}
                  <div className="controls-row">
                    <div className="left-controls">
                      <IconButton 
                        className="control-button"
                        onClick={togglePlay}
                        disabled={isLoading}
                      >
                        {isPlaying ? <Pause /> : <PlayArrow />}
                      </IconButton>

                      <div className="volume-controls">
                        <IconButton 
                          className="control-button"
                          onClick={toggleMute}
                        >
                          {isMuted ? <VolumeOff /> : <VolumeUp />}
                        </IconButton>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={isMuted ? 0 : volume}
                          onChange={handleVolumeChange}
                          className="volume-slider"
                        />
                      </div>

                      <div className="time-display">
                        <Typography variant="body2">
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </Typography>
                      </div>
                    </div>

                    <div className="right-controls">
                      {video.ebookUrl && (
                        <IconButton 
                          className="control-button"
                          onClick={handleDownloadEbook}
                          title="Descarcă eBook"
                        >
                          <PictureAsPdf />
                        </IconButton>
                      )}
                      
                      <IconButton 
                        className="control-button"
                        onClick={toggleFullscreen}
                        title="Ecran complet"
                      >
                        <Fullscreen />
                      </IconButton>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Video Description */}
          <div className="video-description">
            <Typography variant="body1">
              {video.description}
            </Typography>
            
            <div className="video-meta">
              <Typography variant="body2" color="textSecondary">
                Durată: {video.duration} • Lecții: {video.lessons} • Nivel: {video.requiredRank}
              </Typography>
            </div>

            {video.ebookUrl && (
              <div className="ebook-section">
                <Button
                  variant="outlined"
                  startIcon={<GetApp />}
                  onClick={handleDownloadEbook}
                  className="ebook-download-btn"
                >
                  Descarcă eBook asociat
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </div>
    </Dialog>
  );
};

export default VideoPlayerModal;
